'use client'

import * as React from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Filter, MapPin, Users, MoreHorizontal, FolderKanban, AlertTriangle, CheckCircle, Pause } from 'lucide-react'
import { collection, getDocs } from 'firebase/firestore'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { budgetByProject } from 'lib/mock-data'
import { cn } from 'lib/utils'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { db } from '../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProject } from 'lib/access'

const statusColors = {
  planning: 'bg-muted text-muted-foreground',
  active: 'bg-success/15 text-success',
  'at-risk': 'bg-warning/15 text-warning',
  'on-hold': 'bg-muted text-muted-foreground',
  completed: 'bg-primary/15 text-primary',
  closed: 'bg-primary/10 text-primary',
}

const statusIcon = {
  planning: <FolderKanban className="size-3.5" />,
  active: <CheckCircle className="size-3.5" />,
  'at-risk': <AlertTriangle className="size-3.5" />,
  'on-hold': <Pause className="size-3.5" />,
  completed: <CheckCircle className="size-3.5" />,
  closed: <CheckCircle className="size-3.5" />,
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'at-risk', label: 'At Risk' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
]

const statusAliases = {
  on_hold: 'on-hold',
  onhold: 'on-hold',
  atrisk: 'at-risk',
}

function normalizeStatus(status) {
  if (!status) return 'planning'
  const normalized = String(status)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
  return statusAliases[normalized] || normalized
}

function formatStatusLabel(status) {
  return normalizeStatus(status).replace(/-/g, ' ')
}

function clampNumber(value, min = 0, max = 100) {
  return Math.min(Math.max(value, min), max)
}

function getPercentComplete(project) {
  const raw =
    project?.percentComplete ??
    project?.progress ??
    project?.completion ??
    project?.progressPercent
  const parsed = Number(raw)
  if (Number.isFinite(parsed)) return clampNumber(parsed)
  const normalized = normalizeStatus(project?.status)
  if (normalized === 'completed' || normalized === 'closed') return 100
  if (normalized === 'active') return 55
  if (normalized === 'at-risk') return 35
  if (normalized === 'on-hold') return 20
  if (normalized === 'planning') return 10
  return 0
}

function getProjectCode(project) {
  if (!project) return 'PRJ-000'
  return (
    project.code ||
    project.projectCode ||
    project.reference ||
    project.grantCode ||
    `PRJ-${String(project.id || '').slice(0, 5).toUpperCase()}`
  )
}

function getProjectPI(project) {
  return (
    project?.principalInvestigatorName ||
    project?.pi ||
    project?.projectLead ||
    project?.lead ||
    'Unknown'
  )
}

function getProjectLocation(project) {
  return project?.location || project?.department || project?.departmentId || 'Not specified'
}

function getProjectTeam(project) {
  const raw =
    project?.projectMembers ||
    project?.team ||
    project?.members ||
    project?.teamMembers ||
    []
  if (!Array.isArray(raw)) return []
  return raw
    .map((member) => {
      if (typeof member === 'string') return member
      if (member?.name) return member.name
      if (member?.fullName) return member.fullName
      if (member?.email) return member.email
      return 'Member'
    })
    .filter(Boolean)
}

function getProjectTheme(project) {
  const thematic =
    project?.thematicArea ||
    project?.theme ||
    (Array.isArray(project?.strategicPillars) ? project.strategicPillars[0] : null)
  if (thematic) return thematic
  if (project?.type) return project.type.replace(/_/g, ' ')
  return 'General'
}

function getDonorName(project) {
  return project?.donorName || project?.funder || project?.sponsor || project?.donor
}

function getInitials(name) {
  if (!name) return 'NA'
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function EmptyState({ title, description }) {
  return (
    <Card className="border border-dashed border-border">
      <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <FolderKanban className="size-5 text-primary" />
        </div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}

// Manpower/resource data inspired by Progress Monitor reference
const resourceData = [
  { dept: 'Research', required: 15, available: 12 },
  { dept: 'Lab/Field', required: 10, available: 8 },
  { dept: 'Admin', required: 5, available: 5 },
  { dept: 'MEL', required: 4, available: 3 },
]

// Issue tracker data
const issueData = [
  { severity: 'Minimal', count: 3, color: 'oklch(0.55 0.16 150)' },
  { severity: 'Moderate', count: 5, color: 'oklch(0.87 0.15 85)' },
  { severity: 'Critical', count: 2, color: 'oklch(0.577 0.245 27.325)' },
]

// Overall progress
const overallPlanned = 55
const overallActual = 48

export default function ProjectsPage() {
  const { user, accessLevel, canEdit } = useUser()
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [projects, setProjects] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadProjects() {
      try {
        const snapshot = await getDocs(collection(db, 'projects'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setProjects(records)
      } catch {
        setProjects([])
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadProjects()

    return () => {
      isMounted = false
    }
  }, [])

  const limited = accessLevel === 'limited'
  const currentUserId = getUserId(user)
  const visibleProjects = limited
    ? projects.filter((project) => isUserAssociatedWithProject(project, currentUserId))
    : projects

  const filteredProjects = visibleProjects.filter((project) => {
    const title = (project.title || project.name || '').toLowerCase()
    const code = getProjectCode(project).toLowerCase()
    const pi = getProjectPI(project).toLowerCase()
    const matchesSearch =
      title.includes(search.toLowerCase()) ||
      code.includes(search.toLowerCase()) ||
      pi.includes(search.toLowerCase())
    const normalizedStatus = normalizeStatus(project.status)
    const matchesStatus = statusFilter === 'all' || normalizedStatus === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalProjects = visibleProjects.length
  const activeProjects = visibleProjects.filter((project) => normalizeStatus(project.status) === 'active').length
  const atRiskProjects = visibleProjects.filter((project) => normalizeStatus(project.status) === 'at-risk').length
  const completedProjects = visibleProjects.filter((project) => {
    const normalized = normalizeStatus(project.status)
    return normalized === 'completed' || normalized === 'closed'
  }).length

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            Project Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Work breakdown, milestones, deliverables, and coordination
          </p>
        </div>
        {canEdit ? (
          <Button asChild className="bg-primary text-primary-foreground hover:bg-accent gap-2">
            <Link to="/dashboard/projects/new">
              <Plus className="size-4" />
              New Project
            </Link>
          </Button>
        ) : null}
      </div>

      {/* Overview Stats + Progress Gauge Row - Inspired by Progress Monitor */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Projects</p>
            <p className="text-3xl font-bold text-foreground mt-1">{totalProjects}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-success font-medium">Active</p>
            <p className="text-3xl font-bold text-success mt-1">{activeProjects}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-warning font-medium">At Risk</p>
            <p className="text-3xl font-bold text-warning mt-1">{atRiskProjects}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Completed</p>
            <p className="text-3xl font-bold text-primary mt-1">{completedProjects}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-chart-1" />
          </CardContent>
        </Card>
        {/* Overall Progress Card - Inspired by Gauge in Reference */}
        <Card className="border border-border bg-primary/5">
          <CardContent className="p-4 text-center flex flex-col items-center justify-center">
            <p className="text-[10px] uppercase tracking-wider text-primary font-medium">Overall Progress</p>
            <p className="text-3xl font-bold text-primary mt-1">{overallActual}%</p>
            <p className="text-[10px] text-muted-foreground">Against Plan of {overallPlanned}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Monitor Graph - Gantt-style bars inspired by reference */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">Progress Monitor</CardTitle>
          <CardDescription className="text-xs">Project completion tracking with planned vs actual</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2">
            {isLoading ? (
              <p className="text-xs text-muted-foreground">Loading projects...</p>
            ) : visibleProjects.length === 0 ? (
              <p className="text-xs text-muted-foreground">No projects available yet.</p>
            ) : (
              visibleProjects.map((project) => {
                const normalizedStatus = normalizeStatus(project.status)
                const percentComplete = getPercentComplete(project)
                const planned =
                  normalizedStatus === 'completed' || normalizedStatus === 'closed'
                    ? 100
                    : Math.min(percentComplete + 12, 100)
                const projectCode = getProjectCode(project)
                const budget = budgetByProject.find((b) => b.name === projectCode)
                return (
                  <Link
                    key={project.id || projectCode}
                    to={`/dashboard/projects/${project.id}`}
                    className="block"
                  >
                    <div className="flex flex-col gap-1 rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className={cn('text-[10px] gap-1', statusColors[normalizedStatus] || statusColors.planning)}>
                            {statusIcon[normalizedStatus] || statusIcon.planning}
                            {formatStatusLabel(project.status)}
                          </Badge>
                          <span className="text-xs font-medium text-foreground">{projectCode}</span>
                          <span className="text-[10px] text-muted-foreground hidden sm:inline">
                            {project.title || project.name || 'Untitled project'}
                          </span>
                        </div>
                        <span className="text-xs font-bold text-foreground">{percentComplete}%</span>
                      </div>
                      {/* Double bar: planned (muted) vs actual (colored) */}
                      <div className="relative h-5 rounded-md bg-muted/40 overflow-hidden">
                        <div
                          className="absolute top-0 h-full bg-primary/20 rounded-md"
                          style={{ width: `${planned}%` }}
                        />
                        <div
                          className={cn(
                            'absolute top-0 h-full rounded-md flex items-center px-2',
                            normalizedStatus === 'at-risk' ? 'bg-warning/70' :
                            normalizedStatus === 'completed' || normalizedStatus === 'closed' ? 'bg-success/70' :
                            'bg-primary/70'
                          )}
                          style={{ width: `${percentComplete}%` }}
                        >
                          <span className="text-[9px] text-card font-medium truncate">
                            {getProjectPI(project)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3" />
                          {getProjectLocation(project)}
                        </span>
                        <span>Planned: {planned}%</span>
                        <span>Actual: {percentComplete}%</span>
                        {budget && (
                          <span className="ml-auto">
                            Budget: ${(budget.actual / 1000).toFixed(0)}K / ${(budget.budgeted / 1000).toFixed(0)}K
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                )
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resource + Issues Row - Inspired by Progress Monitor */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Manpower Plan vs Actual */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Resource Plan vs Actual</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={resourceData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="dept" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="required" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} name="Required" />
                <Bar dataKey="available" fill="oklch(0.87 0.15 85)" radius={[4, 4, 0, 0]} name="Available" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Critical Issue Tracker */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Critical Issue Tracker</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={180}>
                <PieChart>
                  <Pie data={[{ name: 'Open', value: 10 }, { name: 'Closed', value: 22 }]} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3} strokeWidth={0}>
                    <Cell fill="oklch(0.35 0.12 145)" />
                    <Cell fill="oklch(0.87 0.15 85)" />
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={issueData} barSize={24}>
                    <XAxis dataKey="severity" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" />
                    <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10, fill: 'oklch(0.48 0.03 145)' }}>
                      {issueData.map((entry) => (
                        <Cell key={entry.severity} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Project Views */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search projects, codes, PIs..." className="pl-10 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9">
            <Filter className="mr-2 size-3.5" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="cards">
        <TabsList>
          <TabsTrigger value="cards">Cards</TabsTrigger>
          <TabsTrigger value="table">Table</TabsTrigger>
        </TabsList>

        <TabsContent value="cards" className="mt-4">
          {isLoading ? (
            <EmptyState title="Loading projects..." description="Fetching data from Firestore." />
          ) : filteredProjects.length === 0 ? (
            <EmptyState
              title="No projects found"
              description={
                search || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Create your first project to get started.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredProjects.map((project) => {
                const normalizedStatus = normalizeStatus(project.status)
                const percentComplete = getPercentComplete(project)
                const team = getProjectTeam(project)
                const donorName = getDonorName(project)
                const projectCode = getProjectCode(project)
                return (
                  <Link key={project.id || projectCode} to={`/dashboard/projects/${project.id}`} className="block h-full">
                    <Card className="h-full border border-border hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Badge className={cn('text-[10px] mb-2 gap-1', statusColors[normalizedStatus] || statusColors.planning)}>
                              {statusIcon[normalizedStatus] || statusIcon.planning}
                              {formatStatusLabel(project.status)}
                            </Badge>
                            <CardTitle className="text-sm font-semibold text-foreground leading-tight">
                              {project.title || project.name || 'Untitled project'}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {projectCode}
                              {donorName ? ` | ${donorName}` : ''}
                            </p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={(event) => event.preventDefault()}>
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>View Details</DropdownMenuItem>
                            <DropdownMenuItem onClick={(event) => { event.preventDefault(); event.stopPropagation() }}>Edit Project</DropdownMenuItem>
                            <DropdownMenuItem onClick={(event) => { event.preventDefault(); event.stopPropagation() }}>Workplan</DropdownMenuItem>
                            <DropdownMenuItem onClick={(event) => { event.preventDefault(); event.stopPropagation() }}>Risk Register</DropdownMenuItem>
                          </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex justify-between text-[10px]">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium text-foreground">{percentComplete}%</span>
                          </div>
                          <Progress value={percentComplete} className="h-1.5" />
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="size-3 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">{getProjectLocation(project)}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Users className="size-3 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">PI: {getProjectPI(project)}</span>
                          </div>
                          <div className="flex -space-x-1.5">
                            {team.slice(0, 3).map((member) => (
                              <Avatar key={member} className="size-5 border border-card">
                                <AvatarFallback className="text-[8px] bg-primary/20 text-primary">
                                  {getInitials(member)}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                            {team.length === 0 && (
                              <Avatar className="size-5 border border-card">
                                <AvatarFallback className="text-[8px] bg-primary/20 text-primary">
                                  {getInitials(getProjectPI(project))}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            {team.length > 3 && (
                              <Avatar className="size-5 border border-card">
                                <AvatarFallback className="text-[8px] bg-muted text-muted-foreground">
                                  +{team.length - 3}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        </div>
                        <Badge variant="secondary" className="w-fit text-[10px]">
                          {getProjectTheme(project)}
                        </Badge>
                      </CardContent>
                    </Card>
                  </Link>
                )
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          <Card className="border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Code</TableHead>
                  <TableHead className="text-xs">Title</TableHead>
                  <TableHead className="text-xs">PI</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Progress</TableHead>
                  <TableHead className="text-xs">Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => {
                  const normalizedStatus = normalizeStatus(project.status)
                  const percentComplete = getPercentComplete(project)
                  const projectCode = getProjectCode(project)
                  return (
                    <TableRow key={project.id || projectCode} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="text-xs font-mono font-medium text-foreground">{projectCode}</TableCell>
                      <TableCell className="text-xs text-foreground max-w-[200px] truncate">
                        {project.title || project.name || 'Untitled project'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{getProjectPI(project)}</TableCell>
                      <TableCell>
                        <Badge className={cn('text-[10px]', statusColors[normalizedStatus] || statusColors.planning)}>
                          {formatStatusLabel(project.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={percentComplete} className="h-1 w-16" />
                          <span className="text-[10px] text-muted-foreground">{percentComplete}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{getProjectLocation(project)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
